import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Search } from "components/DesignSystem/Search/Search";
import { MentionIcon } from "components/icons/MentionIcon";
import { ProductChip } from "components/product/ProductChip";
import {
  CHAT_CHANNEL_SEARCHED,
  CHAT_SHOW_UNREAD_CLICKED,
} from "constants/analyticsEvents";
import { DISPLAY_DATE } from "constants/date";
import { useChatFilterContext } from "contexts/ChatFilterContext";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useAnalytics } from "hooks/useAnalytics";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ALL_CHAT_FILTER, UNREAD_CHAT_FILTER } from "constants/chatOtherFilter";
import { ComponentProps, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { parse } from "qs";
import {
  setSearch,
  setSearchActiveState,
  setShowAIChannels,
  setShowMentionedMessages,
  setShowUnrepliedMessages,
  setSortByNewest,
  setUnreadFilter,
} from "store/slices/chatSearch";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { RootState } from "store/store";
import {
  DefaultStreamChatGenerics,
  useChatContext,
  usePaginatedChannels,
} from "stream-chat-react";
import { debounce } from "utils/debouncing";
import { ChannelListFilter } from "./ChannelListFilter";
import { UnrepliedIcon } from "components/icons/UnrepliedIcon";
import { SortUp } from "components/icons/SortUp";
import { SortDown } from "components/icons/SortDown";
import { useAppSelector } from "hooks/useAppSelector";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";

export const ChannelSearch = ({
  hideFilters,
  hideUnreadChip,
  defaultNewestSort,
  unreplied,
  isMyMessages,
}: Pick<ComponentProps<typeof ChannelListFilter>, "hideFilters"> & {
  hideUnreadChip?: boolean;
  defaultNewestSort?: boolean;
  unreplied: boolean;
  isMyMessages?: boolean;
}) => {
  const { client } = useChatContext();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const isUnreadFilterActive = useSelector(
    (state: RootState) => state.chatSearch.unread
  );
  const isSortActiveActive =
    useSelector((state: RootState) => state.chatSearch.sortByNewest) !== null;
  const { filters: values, setFilter: updateFilter } = useChatFilterContext();
  const { setActiveChannel } = useChatContext();
  const {
    showMentionedMessages,
    sortByNewest,
    showUnrepliedMessages,
    showAIChannels,
  } = useSelector((state: RootState) => state.chatSearch);
  const { isCustomer, isAdmin } = useRoleBasedView();
  const [showAppliedFilters, setShowAppliedFilters] = useState(false);
  const isNewestSortActive =
    sortByNewest !== null && defaultNewestSort !== sortByNewest;
  const { unrepliedChannelCount, myUnrepliedChannelCount } = useAppSelector(
    (state) => state.chat
  );

  const onChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      trackEvent(CHAT_CHANNEL_SEARCHED);
    }
    dispatch(setSearch(e.target.value));
    dispatch(setScrollToMessageId(undefined));
  });

  const toggleSortByUnread = () => {
    const enablingTheFilter = !isUnreadFilterActive;
    if (enablingTheFilter) {
      trackEvent(CHAT_SHOW_UNREAD_CLICKED);
      setActiveChannel(undefined);
    }
    dispatch(setUnreadFilter(!isUnreadFilterActive));
  };

  const handleMentionFilter = () => {
    dispatch(setShowMentionedMessages(!showMentionedMessages));
  };

  const toggleAIChannelFilter = () => {
    if (showAIChannels) {
      setActiveChannel(undefined);
    }
    dispatch(setShowAIChannels(!showAIChannels));
  };

  const handleUnrepliedFilter = () => {
    dispatch(setShowUnrepliedMessages(!showUnrepliedMessages));
  };

  const toggleSortByNewest = () => {
    dispatch(setSortByNewest(!sortByNewest));
  };

  const { search } = useLocation();

  useEffect(() => {
    const query = parse(search, { ignoreQueryPrefix: true });
    dispatch(setSearch(""));
    dispatch(setSortByNewest(defaultNewestSort || null));
    if (query.ai === "true") {
      dispatch(setShowAIChannels(true));
    }
    return () => {
      dispatch(setShowMentionedMessages(false));
      dispatch(setSortByNewest(null));
      dispatch(setShowUnrepliedMessages(false));
      dispatch(setShowAIChannels(false));
    };
  }, []);

  const totalAppliedFilters =
    values.CHANNEL.length +
    (values.ENTITY ? 1 : 0) +
    (values.SEASON ? 1 : 0) +
    values.STATUS.length +
    values.COMPANY.length +
    values.AGENT.length +
    (values.START_DATE && values.END_DATE ? 1 : 0) +
    (values.OTHERS === UNREAD_CHAT_FILTER ? 1 : 0) +
    (values.IS_PENDING_ACTION ? 1 : 0);

  const removeUnreadFilter = () => {
    updateFilter("OTHERS", ALL_CHAT_FILTER);
    toggleSortByUnread();
  };

  const removePendingActionFilter = () =>
    updateFilter("IS_PENDING_ACTION", false);

  const count = isMyMessages ? myUnrepliedChannelCount : unrepliedChannelCount;

  return (
    <div className="t-w-full">
      <div className="t-flex t-w-full t-justify-between t-items-center t-gap-2">
        <Search
          onChange={onChange}
          block
          placeholder="Search channel"
          onFocus={() => dispatch(setSearchActiveState(true))}
          onBlur={() => dispatch(setSearchActiveState(false))}
        />

        {isAdmin && !unreplied && (
          <Button
            size="xsmall"
            customType={showAIChannels ? "icon_primary_outlined" : "icon"}
            onClick={toggleAIChannelFilter}
            active={showAIChannels}
          >
            <img
              src={AIGenerateButtonStar}
              alt="AIGenerateButtonStar"
              className="t-size-4"
            />
          </Button>
        )}
        {isAdmin && (
          <div className="t-flex t-gap-2">
            <ToolTip
              text={`Click to sort by ${sortByNewest ? "oldest" : "newest"}`}
            >
              <span>
                <Button
                  size="xsmall"
                  customType={
                    isNewestSortActive ? "icon_primary_outlined" : "icon"
                  }
                  onClick={toggleSortByNewest}
                >
                  {sortByNewest ? (
                    <span
                      className={classNames("", {
                        "t-text-purple": isNewestSortActive,
                        "t-text-text-30": !isNewestSortActive,
                      })}
                    >
                      <SortUp />
                    </span>
                  ) : (
                    <span
                      className={classNames("", {
                        "t-text-purple": isNewestSortActive,
                        "t-text-text-30": !isNewestSortActive,
                      })}
                    >
                      <SortDown />
                    </span>
                  )}
                </Button>
              </span>
            </ToolTip>
          </div>
        )}
        {isAdmin ? (
          <ChannelListFilter
            updateFilter={updateFilter}
            values={values}
            toggleSortByUnread={toggleSortByUnread}
            hideFilters={hideFilters}
          />
        ) : hideUnreadChip ? null : (
          <ProductChip
            onClick={toggleSortByUnread}
            isActive={isUnreadFilterActive}
            onClose={toggleSortByUnread}
            icon={null}
          >
            <div className="t-w-20 t-text-text-60 t-text-body-sm">
              Show Unread
            </div>
          </ProductChip>
        )}
        {!isCustomer && (
          <div className="t-text-text-30">
            <Button
              size="xsmall"
              customType={
                showMentionedMessages ? "icon_primary_outlined" : "icon"
              }
              onClick={handleMentionFilter}
              active={showMentionedMessages}
            >
              <MentionIcon />
            </Button>
          </div>
        )}
        {!isCustomer && !unreplied && (
          <ToolTip text="Unreplied">
            <div className="t-text-text-30 t-relative">
              <Button
                size="xsmall"
                customType={
                  showUnrepliedMessages ? "icon_primary_outlined" : "icon"
                }
                onClick={handleUnrepliedFilter}
                active={showUnrepliedMessages}
              >
                <UnrepliedIcon />
              </Button>{" "}
              {Number(count) > 0 && (
                <span className="t-absolute t-text-[8px] t-top-1 t-left-4 t-bg-red-50 t-h-3 t-w-3 t-text-white t-rounded-full t-flex t-items-center t-justify-center">
                  {count}
                </span>
              )}
            </div>
          </ToolTip>
        )}
      </div>
      {isAdmin && (
        <AnimatePresence>
          <div className="t-flex t-gap-2 t-flex-wrap t-mt-2">
            {Boolean(showAppliedFilters) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="t-flex t-gap-1 t-flex-wrap"
              >
                {values.CHANNEL.length > 0 && (
                  <Chip isActive onRemove={() => updateFilter("CHANNEL", [])}>
                    Channel ({values.CHANNEL.length})
                  </Chip>
                )}

                {values.ENTITY && (
                  <Chip isActive onRemove={() => updateFilter("ENTITY", null)}>
                    Entities ({values.ENTITY.length})
                  </Chip>
                )}

                {values.SEASON && (
                  <Chip isActive onRemove={() => updateFilter("SEASON", null)}>
                    Year ({values.SEASON.length})
                  </Chip>
                )}

                {values.STATUS.length > 0 && (
                  <Chip isActive onRemove={() => updateFilter("STATUS", [])}>
                    Status ({values.STATUS.length})
                  </Chip>
                )}

                {values.COMPANY.length > 0 && (
                  <Chip isActive onRemove={() => updateFilter("COMPANY", [])}>
                    Company ({values.COMPANY.length})
                  </Chip>
                )}

                {values.AGENT.length > 0 && (
                  <Chip isActive onRemove={() => updateFilter("AGENT", [])}>
                    Agent ({values.AGENT.length})
                  </Chip>
                )}

                {values.START_DATE && values.END_DATE && (
                  <Chip
                    isActive
                    onRemove={() => {
                      updateFilter("START_DATE", "");
                      updateFilter("END_DATE", "");
                    }}
                  >
                    {dayjs(values.START_DATE).format(DISPLAY_DATE)} to{" "}
                    {dayjs(values.END_DATE).format(DISPLAY_DATE)}
                  </Chip>
                )}

                {values.OTHERS === UNREAD_CHAT_FILTER && (
                  <Chip isActive onRemove={removeUnreadFilter}>
                    Unread
                  </Chip>
                )}

                {values.IS_PENDING_ACTION && (
                  <Chip isActive onRemove={removePendingActionFilter}>
                    Actions pending
                  </Chip>
                )}
              </motion.div>
            )}

            {Boolean(totalAppliedFilters) && (
              <Button
                size="xsmall"
                customType="link"
                onClick={() => setShowAppliedFilters((p) => !p)}
              >
                {showAppliedFilters
                  ? "Hide filters"
                  : `View filters (${totalAppliedFilters})`}
              </Button>
            )}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
};
